import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faPercent,
  faTag,
  faSearch,
  faRefresh,
  faEye,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons'
import { isMobile } from 'react-device-detect'
import { toast } from 'react-toastify'
import mobileSubheader from '../_layout/elements/mobile-subheader'
import affiliateService from '../../../services/affilate.serrvice'

interface UserCommission {
  betCommission: number
  depositCommission: number
  total: number
}

interface UserItem {
  _id: string
  username: string
  fullName: string
  phone: string
  referrerCode: string
  betPnl: number
  depositPnl: number
  totalProfitLoss: number
  depositCount: number
  totalBets: number
  isLogin: boolean
  commission: UserCommission
}

interface AffiliateData {
  _id: string
  fullName: string
  referralCode: string
  commissionRate: number
  category: string
  createdAt: string
}

const UserComm: React.FC = () => {
  const [users, setUsers] = useState<UserItem[]>([])
  const [affiliateData, setAffiliateData] = useState<AffiliateData | null>(null)
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<UserItem | null>(null)
  const [detailsLoading, setDetailsLoading] = useState(false)

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const res = await affiliateService.getAffiliateUsers()
      if (res.data && res.data.code === 200 && !res.data.error) {
        setUsers(res.data.data.users || [])
        setAffiliateData(res.data.data.affiliate)
      } else {
        toast.error(res.data?.message || 'Failed to fetch users')
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const openDetails = (user: UserItem) => {
    setSelected(user)
    setDetailsLoading(false)
  }

  return (
    <>
      {mobileSubheader.subheaderdesktopadmin('User Commission')}
      <div className='container-fluid'>
        <div className='row'>
          <div className={!isMobile ? 'col-md-12 mt-1' : 'col-md-12 padding-custom'}>
            <div className='card' style={{ marginTop: '10px' }}>
              <div className='card-header bg-primary text-white d-flex justify-content-between align-items-center'>
                <h5 className='mb-0'>User Commission</h5>
                <button className='btn btn-light btn-sm' onClick={fetchUsers}>
                  <FontAwesomeIcon icon={faRefresh} className={loading ? 'fa-spin' : ''} />
                </button>
              </div>

              <div className='card-body'>
                <div className='table-responsive'>
                  <table className='table table-hover'>
                    <thead className='table-dark'>
                      <tr>
                        <th>User</th>
                        <th>Referral Code</th>
                        <th>Commission Rate</th>
                        <th>Category</th>
                        <th>Comm. Type</th>
                        <th>Total Commission</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={7} className='text-center py-4 text-muted'>
                            {loading ? 'Loading...' : 'No users found'}
                          </td>
                        </tr>
                      ) : (
                        users.map((u,i) => {
                          const commissionType =
                            affiliateData?.category === 'deposit' ? 'Deposit' : 'Profit'
                          const totalCommission = u.commission?.total || 0

                          return (
                            <tr key={u._id}>
                              <td>
                                <div className='fw-bold text-primary'>{u.fullName}-{i+1}</div>
                              </td>
                              <td>
                                <span className='badge bg-info text-dark'>
                                  <FontAwesomeIcon icon={faTag} className='me-1' /> {u.referrerCode}
                                </span>
                              </td>
                              <td>
                                <FontAwesomeIcon icon={faPercent} className='me-1 text-success' />
                                <span className='fw-bold text-success'>{affiliateData?.commissionRate}%</span>
                              </td>
                              <td>
                                <span
                                  className={`badge ${affiliateData?.category === 'deposit' ? 'bg-success' : 'bg-warning'
                                    }`}
                                >
                                  {affiliateData?.category}
                                </span>
                              </td>
                              <td>{commissionType}</td>
                              <td>₹ {totalCommission.toFixed(2)}</td>
                              <td>
                                <button className='btn btn-outline-info btn-sm' onClick={() => openDetails(u)}>
                                  <FontAwesomeIcon icon={faEye} />
                                </button>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {selected && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog modal-lg modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header bg-primary text-white'>
                <h5 className='modal-title'>
                  <FontAwesomeIcon icon={faUsers} className='me-2' /> User Details
                </h5>
                <button type='button' className='btn-close btn-close-white' onClick={() => setSelected(null)} />
              </div>
              <div className='modal-body'>
                <div className='row g-4'>
                  <div className='col-md-6'>
                    <div className='card h-100'>
                      <div className='card-header bg-light'>
                        <h6 className='mb-0 text-primary'>User Info</h6>
                      </div>
                      <div className='card-body'>
                        <div className='fw-bold text-primary'>User:{selected.fullName}</div>
                        <div className='text-muted mb-2'>Phone: {selected.phone ? '*******' + selected.phone.slice(-3) : ''}</div>
                        <div className='text-muted mb-2'>Username: N/A</div>
                        <div className='text-muted mb-2'>
                          <FontAwesomeIcon icon={faCalendarAlt} className='me-1' /> Joined:{' '}
                          {'N/A'}
                        </div>
                        <div className='mb-2'>
                          <span className='badge bg-info text-dark'>
                            <FontAwesomeIcon icon={faTag} className='me-1' /> {selected.referrerCode}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='card h-100'>
                      <div className='card-header bg-light'>
                        <h6 className='mb-0 text-primary'>Commission Summary</h6>
                      </div>
                      <div className='card-body'>
                        <div className='d-flex justify-content-between mb-2'>
                          <span>Total Commission</span>
                          <strong>₹ {(selected.commission.total || 0).toFixed(2)}</strong>
                        </div>
                        <div className='d-flex justify-content-between'>
                          <span>Deposit Commission</span>
                          <strong>₹ {(selected.commission.depositCommission || 0).toFixed(2)}</strong>
                        </div>
                        <div className='d-flex justify-content-between'>
                          <span>Bet Commission</span>
                          <strong>₹ {(selected.commission.betCommission || 0).toFixed(2)}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-secondary' onClick={() => setSelected(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default UserComm
